import { GetPageProps, UserRegistrationState } from 'hooks/u002/useUserRegistrationHooks';
import { PageContent } from 'templates/PageContent';
import { TermsParser } from 'organisms/u002/TermsParser';
import { TransitionButton } from 'atoms/TransitionButton';
import { Button } from 'atoms/Button';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import { AppLogoHeader } from 'templates/AppLogoHeader';
import { LabeledInputField } from 'molecules/LabeledInputField';
import { MailAddressSchemaType } from 'commons/auditionForm';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useMailAddressSchema } from 'hooks/u002/useMailAdressSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { FieldErrorMessages } from 'molecules/FieldErrorMessages';
import { useContext, useEffect, useRef, useState } from 'react';
import { ParentalConsentResubmittion } from './ParentalConsentResubmittion';
import { useSendConsentForm } from 'hooks/u002/useSendConsentFormHooks';
import { AppContext } from 'templates/AppContextProvider';
import LoadingModal from 'organisms/LoadingModal';
import MessageModal from 'organisms/MessageModal';
import { UnauthAppFooter } from 'templates/UnauthAppFooter';
import { Form } from 'templates/Form';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useGetAgreementText } from 'hooks/u002/useGetAgreementTextHooks';
import { Checkbox, FormControlLabel } from '@mui/material';
import ReCaptchaErrorModal from 'organisms/ReCaptchaErrorModal';
const label = { inputProps: { 'aria-label': 'Consent Checkbox' } };

type BaseUnderEighteenGuidanceProps = {
  auditionId?: string;
  birth?: Date;
  onBack?: () => void;
};
type WithUserRegistrationProps = GetPageProps<UserRegistrationState, 'UnderEighteenGuidance'>;
type UnderEighteenGuidanceProps = BaseUnderEighteenGuidanceProps | WithUserRegistrationProps;

export const UnderEighteenGuidance: React.FC<UnderEighteenGuidanceProps> = (props) => {
  const { currentLang } = useContext(AppContext);
  const { t: tAuditionForm } = useTranslationWithLang('auditionForm');
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [showRecaptchaModal, setShowRecaptchaModal] = useState(false);
  const [sendConsentForm, loading] = useSendConsentForm();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const representativeUser = queryParams.get('representative');
  const [checked, setChecked] = useState(false);
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);
  const termsRef = useRef<HTMLDivElement>(null);

  const [privacyPolicyConsent, privacyPolicyLoading, privacyPolicyErr] = useGetAgreementText({
    audition_id: props.auditionId ?? '',
    lang: currentLang,
    consent_type: '6', // 6: 保護者プライバシーポリシー同意
  });

  useEffect(() => {
    if (privacyPolicyErr) {
      setModalMessage(tAuditionForm('TU002_050'));
      setShowModal(true);
    }
  }, [privacyPolicyErr]);

  const handleChange = (checked: boolean) => {
    setChecked(checked);
  };

  const handleBack = () => {
    if (props.onBack) {
      props.onBack();
    } else {
      navigate(-1);
    }
  };
  const isUserRegistrationProps = (
    props: UnderEighteenGuidanceProps
  ): props is WithUserRegistrationProps => {
    return 'getValues' in props;
  };

  const getBirthday = () => {
    if (isUserRegistrationProps(props)) {
      return props.getValues('birthday');
    }
    return props.birth;
  };

  const MailAddressSchema = useMailAddressSchema();
  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm<MailAddressSchemaType>({
    resolver: zodResolver(MailAddressSchema),
    shouldFocusError: true,
    defaultValues: {
      privacyPolicyConsent: false,
      mailAddress: '',
    },
  });

  useEffect(() => {
    clearErrors();
  }, [currentLang, clearErrors]);

  useEffect(() => {
    if (privacyPolicyConsent?.consent_text) {
      const handleScroll = () => {
        if (termsRef.current) {
          const { scrollTop, scrollHeight, clientHeight } = termsRef.current;
          if (scrollTop + clientHeight >= scrollHeight - 1) {
            setIsScrolledToEnd(true);
          }
        }
      };

      const checkScrollable = () => {
        if (termsRef.current) {
          const { scrollHeight, clientHeight } = termsRef.current;
          if (scrollHeight <= clientHeight) {
            setIsScrolledToEnd(true);
          }
          handleScroll();
        }
      };

      const termsElement = termsRef.current;
      if (termsElement) {
        const resizeObserver = new ResizeObserver(checkScrollable);
        resizeObserver.observe(termsElement);
        termsElement.addEventListener('scroll', handleScroll);
        checkScrollable();

        return () => {
          resizeObserver.disconnect();
          termsElement.removeEventListener('scroll', handleScroll);
        };
      }
    }
  }, [privacyPolicyConsent?.consent_text]);

  const onSubmit: SubmitHandler<MailAddressSchemaType> = async (form) => {
    if (!executeRecaptcha) {
      setModalMessage(tAuditionForm('TU002_108'));
      setShowModal(true);
      return;
    }
    const recaptchaToken = await executeRecaptcha('submit_consent_form');
    await sendConsentForm({
      request: {
        mail: form.mailAddress,
        path: window.location.origin + window.location.pathname,
        birth: getBirthday() ?? null,
        lang: currentLang,
        auditionId: props.auditionId ?? '',
        representative_user: representativeUser ?? '',
        recaptcha_token: recaptchaToken,
      },
      onSuccess: () => {
        setIsSubmitted(true);
      },
      onError: (error) => {
        console.log({ error });
        if (error instanceof Error) {
          if (error.message === 'reCAPTCHA verification failed') {
            setShowRecaptchaModal(true);
            return;
          }
          setModalMessage(
            error.message === 'MM002F0093E'
              ? tAuditionForm('TU002_049')
              : error.message === 'MM002F0108E'
                ? tAuditionForm('TU002_060')
                : tAuditionForm('TU002_050')
          );
          setShowModal(true);
        }
      },
    });
  };
  if (isSubmitted) {
    return (
      <ParentalConsentResubmittion
        setIsSubmitted={setIsSubmitted}
        onClickSubmit={handleSubmit(onSubmit)}
      />
    );
  }

  return (
    <>
      {showModal && (
        <MessageModal
          message={modalMessage}
          closeLabel={tAuditionForm('TU002_032')}
          onClose={() => setShowModal(false)}
        />
      )}
      {showRecaptchaModal && <ReCaptchaErrorModal onClose={() => setShowRecaptchaModal(false)} />}
      {(loading || privacyPolicyLoading) && <LoadingModal />}
      <AppLogoHeader />
      <div role="heading" aria-level={1} aria-live="assertive" className="sr-only">
        {tAuditionForm('TU002_086')}
      </div>
      <PageContent>
        <form onSubmit={handleSubmit(onSubmit)} aria-labelledby="form-title">
          <Form title={tAuditionForm('TU002_086')} id="form-title">
            <div
              className="max-h-96 overflow-y-scroll"
              ref={termsRef}
              aria-label={tAuditionForm('TU002_086')}
              tabIndex={0}
            >
              <TermsParser text={privacyPolicyConsent?.consent_text ?? ''} />
            </div>
            <FormControlLabel
              control={
                <Checkbox
                  color="secondary"
                  {...label}
                  {...register('privacyPolicyConsent')}
                  checked={checked}
                  onChange={(e) => {
                    handleChange(e.target.checked);
                    register('privacyPolicyConsent').onChange(e);
                  }}
                  disabled={!isScrolledToEnd}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      setChecked(!checked);
                    }
                  }}
                  aria-required="true"
                />
              }
              label={tAuditionForm('TU002_046')}
              className="text-[--c-text] flex justify-center items-center"
            />
            <FieldErrorMessages
              messages={[errors.privacyPolicyConsent?.message]}
              aria-live="polite"
            />

            <div className={'flex justify-center items-center'}></div>
            <div
              className="max-h-96 overflow-y-scroll"
              aria-label={tAuditionForm('TU002_090')}
              tabIndex={0}
            >
              <TermsParser text={tAuditionForm('TU002_090')} />
            </div>
            <div className="w-full">
              <LabeledInputField
                title={tAuditionForm('TU002_091')}
                ariaLabel={tAuditionForm('TU002_091')}
                placeholder={tAuditionForm('TU002_011')}
                {...register('mailAddress')}
                className="w-full"
                autoComplete="email"
                id="mailAddress"
                aria-required="true"
                aria-invalid={!!errors.mailAddress}
                aria-describedby={errors.mailAddress ? 'mailAddress-error' : undefined}
              />
            </div>
            <FieldErrorMessages messages={[errors.mailAddress?.message]} aria-live="polite" />

            <div className={'flex justify-center items-center'}>
              <TransitionButton
                label={tAuditionForm('TU002_048')}
                onClick={handleBack}
                className="w-[10rem] mr-6"
                aria-label={tAuditionForm('TU002_048')}
              />
              <Button
                label={tAuditionForm('TU002_078')}
                className={'w-[10rem]'}
                type="submit"
                aria-label={tAuditionForm('TU002_078')}
              />
            </div>
          </Form>
        </form>
      </PageContent>
      <UnauthAppFooter />
    </>
  );
};
